import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  LinearProgress,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from "@mui/material";
import { Refresh, DeleteSweep, Replay } from "@mui/icons-material";
import { toast } from "react-toastify";
import theme from "../../theme/theme";
import {
  fetchServerStats,
  retryFailedJobs,
  flushFailedJobs,
} from "../../api/ServerService";

const formatBytes = (b) => {
  if (b == null || Number.isNaN(b)) return "—";
  if (b < 1024) return `${b} B`;
  const units = ["KB", "MB", "GB", "TB", "PB"];
  let v = b / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v >= 100 ? 0 : v >= 10 ? 1 : 2)} ${units[i]}`;
};

const ago = (seconds) => {
  if (seconds == null) return "—";
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
};

const toneColor = (tone) =>
  tone === "danger" ? "#d32f2f" : tone === "warning" ? "#ed6c02" : "#2e7d32";

const Stat = ({ label, value, tone }) => (
  <Box sx={{ minWidth: 140 }}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography
      variant="h6"
      fontWeight="700"
      sx={{ color: tone ? toneColor(tone) : "text.primary" }}
    >
      {value ?? "—"}
    </Typography>
  </Box>
);

const KeyVal = ({ label, value }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      py: 0.75,
      borderBottom: "1px dashed",
      borderColor: "divider",
    }}
  >
    <Typography variant="caption" color="text.secondary" fontFamily="monospace">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight="600">
      {value ?? "—"}
    </Typography>
  </Box>
);

const SectionCard = ({ title, subtitle, action, children }) => (
  <Paper sx={{ p: 3, borderRadius: theme.paper.borderRadius }}>
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 2,
        gap: 1,
        flexWrap: "wrap",
      }}
    >
      <Box>
        <Typography variant="subtitle1" fontWeight="700" color="primary">
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
      {action}
    </Box>
    {children}
  </Paper>
);

const ServerPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await fetchServerStats();
      setData(res.data ?? res);
    } catch (error) {
      toast.error(error.message || "Failed to load server stats");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleRetry = async () => {
    setActing(true);
    try {
      await retryFailedJobs();
      toast.success("Failed jobs queued for retry");
      load(true);
    } catch (error) {
      toast.error(error.message || "Failed to retry jobs");
    } finally {
      setActing(false);
    }
  };

  const handleFlush = async () => {
    if (
      !window.confirm(
        "Permanently delete all failed job records? This cannot be undone.",
      )
    )
      return;
    setActing(true);
    try {
      await flushFailedJobs();
      toast.success("Failed jobs cleared");
      load(true);
    } catch (error) {
      toast.error(error.message || "Failed to clear jobs");
    } finally {
      setActing(false);
    }
  };

  const disk = data?.disk || {};
  const storage = data?.storage || {};
  const database = data?.database || {};
  const php = data?.php || {};
  const app = data?.app || {};
  const queue = data?.queue || {};
  const sys = data?.system || {};
  const mem = sys.memory;
  const swap = sys.swap;

  const usedPct = disk.total
    ? Math.min(100, (disk.used / disk.total) * 100)
    : null;
  const diskTone =
    usedPct == null
      ? "ok"
      : usedPct >= 90
        ? "danger"
        : usedPct >= 75
          ? "warning"
          : "ok";

  const memPct = mem?.total
    ? Math.min(100, (mem.used / mem.total) * 100)
    : null;
  const memTone =
    memPct == null
      ? "ok"
      : memPct >= 90
        ? "danger"
        : memPct >= 75
          ? "warning"
          : "ok";
  const swapPct = swap?.total
    ? Math.min(100, (swap.used / swap.total) * 100)
    : null;
  // Heavy swap use on a small VM = RAM pressure, even if RAM "available" looks ok.
  const swapTone =
    swapPct == null
      ? "ok"
      : swapPct >= 50
        ? "danger"
        : swapPct >= 20
          ? "warning"
          : "ok";
  // Load relative to cores: >1.0 per core means the CPU is saturated.
  const loadRatio =
    sys.load && sys.cpu_cores ? sys.load["1m"] / sys.cpu_cores : null;
  const loadTone =
    loadRatio == null
      ? "ok"
      : loadRatio >= 1
        ? "danger"
        : loadRatio >= 0.7
          ? "warning"
          : "ok";

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box>
          <Box component="h1" sx={{ m: 0 }}>
            Server
          </Box>
          <Typography variant="caption" color="text.secondary">
            Disk, runtime, and background queue health
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          disabled={loading}
          onClick={() => load()}
          sx={{
            borderRadius: theme.button.borderRadius,
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          {loading ? "Refreshing..." : "Refresh"}
        </Button>
      </Box>

      {loading && !data ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Skeleton variant="rounded" height={140} />
          <Skeleton variant="rounded" height={200} />
          <Skeleton variant="rounded" height={140} />
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {/* CPU + memory */}
          <SectionCard
            title="CPU & Memory"
            subtitle={sys.cpu_cores ? `${sys.cpu_cores} vCPU` : "Live host load"}
          >
            <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap", mb: 2 }}>
              <Stat
                label="Load (1m)"
                value={sys.load ? sys.load["1m"] : "—"}
                tone={loadTone !== "ok" ? loadTone : undefined}
              />
              <Stat
                label="Load 5m / 15m"
                value={sys.load ? `${sys.load["5m"]} / ${sys.load["15m"]}` : "—"}
              />
              <Stat
                label="RAM Used"
                value={
                  mem
                    ? `${formatBytes(mem.used)} / ${formatBytes(mem.total)}`
                    : "—"
                }
                tone={memTone !== "ok" ? memTone : undefined}
              />
              <Stat
                label="Swap Used"
                value={
                  swap
                    ? `${formatBytes(swap.used)} / ${formatBytes(swap.total)}`
                    : "—"
                }
                tone={swapTone !== "ok" ? swapTone : undefined}
              />
            </Box>
            {memPct != null && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  RAM ({memPct.toFixed(0)}%)
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={memPct}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: toneColor(memTone),
                    },
                  }}
                />
              </Box>
            )}
            {swapPct != null && swapPct >= 20 && (
              <Typography variant="caption" sx={{ color: toneColor(swapTone) }}>
                Swap at {swapPct.toFixed(0)}% — the server is leaning on swap, a
                sign it&apos;s short on RAM. If this stays high, size up.
              </Typography>
            )}
            {loadTone === "danger" && (
              <Typography
                variant="caption"
                sx={{ color: toneColor("danger"), display: "block" }}
              >
                Load exceeds CPU core count — the server is saturated.
              </Typography>
            )}
            {!sys.load && !mem && (
              <Typography variant="caption" color="text.secondary">
                Host metrics unavailable (non-Linux host).
              </Typography>
            )}
          </SectionCard>

          {/* Queue / worker health */}
          <SectionCard
            title="Background Queue"
            subtitle={`Connection: ${queue.connection || "—"}`}
            action={
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Replay />}
                  disabled={!queue.failed_total || acting}
                  onClick={handleRetry}
                  sx={{
                    borderRadius: theme.button.borderRadius,
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Retry Failed
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteSweep />}
                  disabled={!queue.failed_total || acting}
                  onClick={handleFlush}
                  sx={{
                    borderRadius: theme.button.borderRadius,
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Clear Failed
                </Button>
              </Box>
            }
          >
            <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap", mb: 2 }}>
              <Box sx={{ minWidth: 140 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Worker
                </Typography>
                <Chip
                  label={queue.worker_ok ? "Healthy" : "Check"}
                  size="small"
                  sx={{
                    mt: 0.5,
                    fontWeight: 700,
                    backgroundColor: queue.worker_ok ? "#2e7d3218" : "#d32f2f18",
                    color: queue.worker_ok ? "#2e7d32" : "#d32f2f",
                  }}
                />
              </Box>
              <Stat
                label="Pending Jobs"
                value={(queue.pending ?? 0).toLocaleString()}
              />
              <Stat
                label="Oldest Waiting"
                value={ago(queue.oldest_pending_seconds)}
              />
              <Stat
                label="Failed Jobs"
                value={(queue.failed_total ?? 0).toLocaleString()}
                tone={queue.failed_total ? "danger" : undefined}
              />
            </Box>
            {!queue.worker_ok && (
              <Typography
                variant="caption"
                sx={{ color: toneColor("danger"), display: "block", mb: 1 }}
              >
                Jobs are piling up and not draining — the queue worker may be
                down. Check the <code>queue:work</code> process on the server.
              </Typography>
            )}
            <Typography variant="body2" fontWeight="600" sx={{ mb: 1 }}>
              Recent Failures ({queue.failed_total ?? 0})
            </Typography>
            {!queue.recent_failed?.length ? (
              <Typography variant="caption" color="text.secondary">
                No failed jobs.
              </Typography>
            ) : (
              <TableContainer
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Failed At</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Queue</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {queue.recent_failed.map((j) => (
                      <TableRow key={j.uuid}>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {j.failed_at}
                        </TableCell>
                        <TableCell>{j.queue}</TableCell>
                        <TableCell
                          sx={{ fontFamily: "monospace", fontSize: 12 }}
                        >
                          {j.error}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </SectionCard>

          {/* Disk */}
          <SectionCard
            title="Disk Usage"
            subtitle="Whole partition hosting app storage"
          >
            {usedPct != null && (
              <LinearProgress
                variant="determinate"
                value={usedPct}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  mb: 2,
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: toneColor(diskTone),
                  },
                }}
              />
            )}
            <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              <Stat label="Total" value={formatBytes(disk.total)} />
              <Stat label="Used" value={formatBytes(disk.used)} />
              <Stat
                label="Free"
                value={formatBytes(disk.free)}
                tone={diskTone !== "ok" ? diskTone : undefined}
              />
              <Stat
                label="% Used"
                value={usedPct == null ? "—" : `${usedPct.toFixed(1)}%`}
                tone={diskTone !== "ok" ? diskTone : undefined}
              />
            </Box>
          </SectionCard>

          {/* Storage + runtime */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 2.5,
            }}
          >
            <SectionCard title="App Storage & Database">
              <KeyVal
                label="Stored files"
                value={(storage.file_count ?? 0).toLocaleString()}
              />
              <KeyVal
                label="Storage size"
                value={formatBytes(storage.total_bytes)}
              />
              <KeyVal
                label="Database size"
                value={formatBytes(database.size_bytes)}
              />
              <KeyVal label="Database tables" value={database.tables} />
            </SectionCard>
            <SectionCard title="Runtime">
              <KeyVal label="PHP" value={php.version} />
              <KeyVal label="Laravel" value={app.laravel_version} />
              <KeyVal label="Environment" value={app.environment} />
              <KeyVal label="Timezone" value={app.timezone} />
              <KeyVal
                label="upload_max_filesize"
                value={php.upload_max_filesize}
              />
              <KeyVal label="post_max_size" value={php.post_max_size} />
              <KeyVal label="memory_limit" value={php.memory_limit} />
            </SectionCard>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ServerPage;
